/**
 * Social Security Fund (SSF), Provident Fund (PF), and Citizen Investment
 * Trust (CIT) contributions.
 *
 * **SSF**: 31% total = 11% employee + 20% employer. Sub-allocation:
 *   medical 3.22%, accident 1.40%, dependent 0.27%, retirement 26.11%.
 *
 * **PF**: standard 10% + 10% employer match.
 *
 * **CIT**: voluntary, capped per `deductionCaps.retirement(Ssf)`.
 *
 * @example
 * calculateSSF(50000)
 * // { employee: 5500, employer: 10000, total: 15500, breakdown: {...} }
 *
 * calculatePF(50000)
 * // { employee: 5000, employer: 5000, total: 10000 }
 */

import { getRates } from "./data/index.js";
import type { FiscalYear } from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** SSF result. */
export interface SsfResult {
  readonly basicSalary: number;
  readonly employee: number;
  readonly employer: number;
  readonly total: number;
  readonly breakdown: {
    readonly medical: number;
    readonly accident: number;
    readonly dependent: number;
    readonly retirement: number;
  };
  readonly fiscalYear: FiscalYear;
}

/**
 * Calculate SSF contribution on a basic salary amount.
 * @param basicSalary - Monthly OR annual basic salary, library is unit-agnostic.
 *                      Result will be in the same units.
 */
export function calculateSSF(
  basicSalary: number,
  options: { fiscalYear?: FiscalYear } = {},
): SsfResult {
  ensureNonNegative(basicSalary, "calculateSSF");
  const rates = getRates(options.fiscalYear);
  const ssf = rates.ssf;
  const total = basicSalary * (ssf.employee + ssf.employer);
  const breakdown = {
    medical: r2(basicSalary * ssf.breakdown.medical),
    accident: r2(basicSalary * ssf.breakdown.accident),
    dependent: r2(basicSalary * ssf.breakdown.dependent),
    retirement: r2(basicSalary * ssf.breakdown.retirement),
  };
  return {
    basicSalary: r2(basicSalary),
    employee: r2(basicSalary * ssf.employee),
    employer: r2(basicSalary * ssf.employer),
    total: r2(total),
    breakdown,
    fiscalYear: rates.fiscalYear,
  };
}

/** PF result. */
export interface PfResult {
  readonly basicSalary: number;
  readonly employee: number;
  readonly employer: number;
  readonly total: number;
  readonly fiscalYear: FiscalYear;
}

/**
 * Calculate Provident Fund contribution.
 * @param employeeRateOverride Custom employee rate (default 10%); employer matches.
 */
export function calculatePF(
  basicSalary: number,
  options: { fiscalYear?: FiscalYear; employeeRate?: number; employerRate?: number } = {},
): PfResult {
  ensureNonNegative(basicSalary, "calculatePF");
  const rates = getRates(options.fiscalYear);
  const empRate = options.employeeRate ?? rates.pf.employee;
  const erRate = options.employerRate ?? rates.pf.employer;
  return {
    basicSalary: r2(basicSalary),
    employee: r2(basicSalary * empRate),
    employer: r2(basicSalary * erRate),
    total: r2(basicSalary * (empRate + erRate)),
    fiscalYear: rates.fiscalYear,
  };
}

/** CIT contribution capped at the retirement deduction limit. */
export interface CitResult {
  readonly contribution: number;
  readonly cappedContribution: number;
  readonly cap: number;
  readonly excess: number;
  readonly fiscalYear: FiscalYear;
}

/**
 * Compute the deductible portion of a CIT contribution. Excess over the cap
 * is still deposited but not deductible.
 */
export function calculateCIT(
  contribution: number,
  options: { fiscalYear?: FiscalYear; isSSFMember?: boolean } = {},
): CitResult {
  ensureNonNegative(contribution, "calculateCIT");
  const rates = getRates(options.fiscalYear);
  const cap = options.isSSFMember
    ? rates.deductionCaps.retirementSsf
    : rates.deductionCaps.retirement;
  const cappedContribution = Math.min(contribution, cap);
  return {
    contribution: r2(contribution),
    cappedContribution: r2(cappedContribution),
    cap,
    excess: r2(Math.max(0, contribution - cap)),
    fiscalYear: rates.fiscalYear,
  };
}
